import React, { useCallback } from 'react';
import { useWatch, type Control } from 'react-hook-form';
import { View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { Button, showAlert, Text } from '@/components/ui';
import Modal from '@/components/ui/modal/modal';
import { STRINGS } from '@/constants/strings';
import {
  GuardianContactUsFields,
  GuardianProfileActionHeader,
} from '@/features/guardian-profile/components';
import { useGuardianContactUsForm } from '@/features/guardian-profile/hooks';
import type { GuardianContactUsForm } from '@/features/guardian-profile/schemas/guardian-contact-us-schema';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { useSubmitContactMutation } from '@/queries/use-submit-contact-mutation';
import { useUiStore } from '@/stores';
import { colors, guardianColors } from '@/theme/colors';
import { useShallow } from 'zustand/shallow';

const strings = STRINGS.guardian.contactUs;

/** BR-01 — block Submit until both required fields (Name, Email) are filled. */
function useRequiredFilled(control: Control<GuardianContactUsForm>) {
  const name = useWatch({ control, name: 'name' });
  const email = useWatch({ control, name: 'email' });
  return Boolean(name?.trim()) && Boolean(email?.trim());
}

/**
 * Sends the Contact Us request via POST /api/v1/contact, showing the global loader while
 * in flight and a success alert on completion. Backend errors surface through the shared
 * server-error popup, so we simply skip the success path on failure.
 */
function useContactSubmit(onSuccess: () => void) {
  const { mutateAsync, isPending } = useSubmitContactMutation();
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(state => ({
      showGlobalLoading: state.showGlobalLoading,
      hideGlobalLoading: state.hideGlobalLoading,
    })),
  );

  const submit = useCallback(
    async (values: GuardianContactUsForm) => {
      try {
        showGlobalLoading();
        await mutateAsync({
          name: values.name.trim(),
          questionType: values.questionType,
          message: values.message?.trim() || undefined,
        });
        showAlert({
          title: strings.successTitle,
          message: strings.successMessage,
          buttons: [{ text: strings.ok, onPress: onSuccess }],
        });
      } catch {
        // Backend errors are surfaced by the shared server-error interceptor.
      } finally {
        hideGlobalLoading();
      }
    },
    [hideGlobalLoading, mutateAsync, onSuccess, showGlobalLoading],
  );

  return { submit, submitting: isPending };
}

/** Phone: full-screen Contact Us form reached from Help & Support. */
export function GuardianContactUsScreen() {
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useGuardianStackNavigation();
  const styles = useStyles();
  const { control, handleSubmit } = useGuardianContactUsForm();
  const canSubmit = useRequiredFilled(control);
  const onSuccess = useCallback(() => navigation.goBack(), [navigation]);
  const { submit, submitting } = useContactSubmit(onSuccess);

  const onSubmit = useCallback(() => handleSubmit(submit)(), [handleSubmit, submit]);

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <GuardianProfileActionHeader
        title={strings.title}
        isTablet={isTablet}
        topInset={insets.top}
        onBack={navigation.goBack}
      />

      <KeyboardAwareScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        bottomOffset={scale(12)}>
        <GuardianContactUsFields control={control} />
      </KeyboardAwareScrollView>

      <Stack paddingX={scale(16)} paddingTop={scale(12)} paddingBottom={insets.bottom + scale(16)}>
        <Button onPress={onSubmit} disabled={!canSubmit || submitting} style={styles.submitButton}>
          <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
            {strings.submit}
          </Button.Text>
        </Button>
      </Stack>
    </Stack>
  );
}

type GuardianContactUsPopupProps = {
  open: boolean;
  onClose: () => void;
};

/** Tablet: Contact Us as a centered popup (Cancel / Submit) over Help & Support. */
export function GuardianContactUsPopup({ open, onClose }: GuardianContactUsPopupProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const { control, handleSubmit, reset } = useGuardianContactUsForm();
  const canSubmit = useRequiredFilled(control);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const { submit, submitting } = useContactSubmit(handleClose);

  const onSubmit = useCallback(() => handleSubmit(submit)(), [handleSubmit, submit]);

  if (!open) return null;

  return (
    <Modal visible={open} onBackdropPress={handleClose} onBackButtonPress={handleClose}>
      <View style={styles.popupCard}>
        <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.popupTitle}>
          {strings.title}
        </Text>

        <KeyboardAwareScrollView
          style={styles.popupScroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          bottomOffset={scale(12)}>
          <GuardianContactUsFields control={control} />
        </KeyboardAwareScrollView>

        <Stack direction="row" gap={scale(16)} paddingTop={scale(30)}>
          <Button
            variant="outline"
            onPress={handleClose}
            style={[styles.popupButton, styles.popupCancel]}>
            <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
              {strings.cancel}
            </Button.Text>
          </Button>
          <Button
            onPress={onSubmit}
            disabled={!canSubmit || submitting}
            style={[styles.popupButton, styles.popupSubmit]}>
            <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
              {strings.submit}
            </Button.Text>
          </Button>
        </Stack>
      </View>
    </Modal>
  );
}

const useStyles = createStyles(({ scale, deviceWidth, deviceHeight }) => ({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: scale(16),
    paddingTop: scale(12),
    paddingBottom: scale(24),
  },
  submitButton: {
    width: '100%',
    borderRadius: scale(12),
    padding: scale(10),
    backgroundColor: guardianColors['Main/Main'],
    shadowColor: '#292757',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  popupCard: {
    width: Math.min(scale(520), deviceWidth * 0.7),
    maxHeight: deviceHeight * 0.85,
    backgroundColor: colors['0'],
    borderRadius: scale(20),
    paddingHorizontal: scale(20),
    paddingTop: scale(16),
    paddingBottom: scale(18),
  },
  popupTitle: {
    color: guardianColors['10'],
    textAlign: 'center',
    marginBottom: scale(35),
  },
  popupScroll: {
    flexGrow: 0,
  },
  popupButton: {
    flex: 1,
    borderRadius: scale(12),
    padding: scale(10),
  },
  // Cancel — outline (0.5px #0033A1 border, white fill, soft black shadow).
  popupCancel: {
    backgroundColor: colors['0'],
    borderWidth: scale(0.5),
    borderColor: guardianColors['Main/Main'],
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
  // Submit — filled Main/Main with the branded shadow.
  popupSubmit: {
    backgroundColor: guardianColors['Main/Main'],
    shadowColor: '#292757',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 2,
  },
}));

export default GuardianContactUsScreen;
